// src/pages/Admin/DossierInscription/components/CreateDossierInscriptionDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaFileAlt,
  FaGoogleDrive,
  FaSave,
  FaSearch,
  FaTimes,
  FaUserCheck,
} from "react-icons/fa";
import { toast } from "react-toastify";

import DossierDrawer from "./DossierDrawer";

import { createDossierInscription } from "../../../../../services/dossierServices";
import { getAllAdherentsList } from "../../../../../services/adherentServices";

import { extractArray, normalizeText, todayISO } from "../dossier.helpers";

const initialForm = {
  inscription_adherent_id: "",
  cm_url: "",
  date_cm: "",
  validate_cm: false,
  formulaire_licence_a_signer: "",
  formulaire_licence_signe: "",
  licence_url: "",
  validate_licence: false,
  validate_reglement: false,
  lien_drive: "",
};

function isValidUrlOrPath(value) {
  const text = String(value || "").trim();

  if (!text) return true;
  if (text.startsWith("/uploads/")) return true;

  try {
    const url = new URL(text);
    return ["http:", "https:"].includes(url.protocol) && Boolean(url.hostname);
  } catch {
    return false;
  }
}

function orNull(value) {
  const text = String(value ?? "").trim();
  return text === "" ? null : text;
}

function FieldLabel({ children, required = false }) {
  return (
    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SectionCard({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-900 dark:text-white">{title}</h3>

          {subtitle ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>

      {children}
    </div>
  );
}

function BigCheck({ name, checked, onChange, label, helper }) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
        checked
          ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10"
          : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900"
      }`}
    >
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="mt-1 h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
      />

      <span className="min-w-0">
        <span className="block text-sm font-bold text-slate-900 dark:text-white">
          {label}
        </span>

        {helper ? (
          <span className="mt-0.5 block text-xs leading-5 text-slate-500 dark:text-slate-400">
            {helper}
          </span>
        ) : null}
      </span>
    </label>
  );
}

function AdherentPicker({ list = [], value, onChange, disabled }) {
  const inputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const indexed = useMemo(() => {
    return extractArray(list)
      .map((adherent) => {
        const fullName = `${adherent?.nom || ""} ${adherent?.prenom || ""}`.trim();

        return {
          id: adherent.id,
          fullName,
          email: adherent?.email || "",
          licence: adherent?.licence_number || "",
          saison: adherent?.saison_active || "",
          raw: adherent,
          key: normalizeText(
            [
              fullName,
              adherent?.email,
              adherent?.licence_number,
              adherent?.commune,
              adherent?.id,
            ]
              .filter(Boolean)
              .join(" ")
          ),
        };
      })
      .sort((a, b) =>
        a.fullName.localeCompare(b.fullName, "fr", { sensitivity: "base" })
      );
  }, [list]);

  const selected = useMemo(() => {
    return indexed.find((item) => String(item.id) === String(value)) || null;
  }, [indexed, value]);

  useEffect(() => {
    if (!open) {
      setQuery(selected?.fullName || "");
    }
  }, [selected, open]);

  const results = useMemo(() => {
    const q = normalizeText(query);

    if (!q) return indexed.slice(0, 60);

    const tokens = q.split(/\s+/).filter(Boolean);

    return indexed
      .filter((item) => tokens.every((token) => item.key.includes(token)))
      .slice(0, 60);
  }, [indexed, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const choose = (item) => {
    onChange?.(item?.id || "");
    setQuery(item?.fullName || "");
    setOpen(false);
    inputRef.current?.blur();
  };

  const clear = () => {
    onChange?.("");
    setQuery("");
    setOpen(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (!open && ["ArrowDown", "Enter"].includes(event.key)) {
      setOpen(true);
      event.preventDefault();
      return;
    }

    if (!open) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((value) => Math.min(value + 1, results.length - 1));
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((value) => Math.max(value - 1, 0));
    }

    if (event.key === "Enter") {
      event.preventDefault();
      choose(results[highlight]);
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <FieldLabel required>Adhérent</FieldLabel>

      {selected ? (
        <div className="mb-2 flex items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-500/20 dark:bg-emerald-500/10">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
              {selected.fullName}
            </p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">
              ID {selected.id}
              {selected.saison ? ` · Saison ${selected.saison}` : ""}
              {selected.email ? ` · ${selected.email}` : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={clear}
            className="shrink-0 rounded-xl bg-white px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Changer
          </button>
        </div>
      ) : null}

      <div className="relative">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

        <input
          ref={inputRef}
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="Rechercher nom, prénom, email, licence..."
          className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        />
      </div>

      {open ? (
        <div
          className="absolute z-[9999] mt-2 max-h-80 w-full overflow-auto rounded-[22px] border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-950"
          onMouseDown={(event) => event.preventDefault()}
        >
          {disabled ? (
            <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
              Chargement des adhérents...
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
              Aucun adhérent trouvé.
            </div>
          ) : (
            results.map((item, index) => (
              <button
                type="button"
                key={item.id}
                onClick={() => choose(item)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition ${
                  index === highlight
                    ? "bg-indigo-50 dark:bg-indigo-500/10"
                    : "hover:bg-slate-50 dark:hover:bg-slate-900"
                }`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                    {item.fullName}
                  </p>

                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                    ID {item.id}
                    {item.saison ? ` · Saison ${item.saison}` : ""}
                    {item.email ? ` · ${item.email}` : ""}
                  </p>
                </div>

                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  Choisir
                </span>
              </button>
            ))
          )}

          {!disabled && results.length >= 60 ? (
            <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
              Affichage limité à 60 résultats. Affinez la recherche.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function CreateDossierInscriptionDrawer({
  isOpen,
  onClose,
  refreshList,
}) {
  const [formData, setFormData] = useState(initialForm);
  const [adherents, setAdherents] = useState([]);

  const [loadingAdherents, setLoadingAdherents] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(initialForm);
    setSaving(false);

    const loadAdherents = async () => {
      try {
        setLoadingAdherents(true);

        const response = await getAllAdherentsList();
        setAdherents(extractArray(response));
      } catch (error) {
        console.error("Erreur chargement adhérents :", error);
        toast.error("Erreur lors du chargement des adhérents.");
      } finally {
        setLoadingAdherents(false);
      }
    };

    loadAdherents();
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === "validate_cm") {
      setFormData((prev) => ({
        ...prev,
        validate_cm: checked,
        date_cm: checked && !prev.date_cm ? todayISO() : prev.date_cm,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const canSubmit = useMemo(() => {
    if (saving || loadingAdherents) return false;
    if (!formData.inscription_adherent_id) return false;

    const urls = [
      formData.cm_url,
      formData.formulaire_licence_a_signer,
      formData.formulaire_licence_signe,
      formData.licence_url,
      formData.lien_drive,
    ];

    return urls.every(isValidUrlOrPath);
  }, [formData, saving, loadingAdherents]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) {
      toast.error("Veuillez sélectionner un adhérent et vérifier les URLs.");
      return;
    }

    try {
      setSaving(true);

      const adherentId = Number(formData.inscription_adherent_id);

      const onlyAdherent =
        !formData.cm_url &&
        !formData.date_cm &&
        !formData.formulaire_licence_a_signer &&
        !formData.formulaire_licence_signe &&
        !formData.licence_url &&
        !formData.lien_drive &&
        !formData.validate_cm &&
        !formData.validate_licence &&
        !formData.validate_reglement;

      if (onlyAdherent) {
        await createDossierInscription(adherentId);
      } else {
        const payload = {
          cm_url: orNull(formData.cm_url),
          date_cm: formData.date_cm || null,
          validate_cm: Boolean(formData.validate_cm),
          formulaire_licence_a_signer: orNull(
            formData.formulaire_licence_a_signer
          ),
          formulaire_licence_signe: orNull(formData.formulaire_licence_signe),
          licence_url: orNull(formData.licence_url),
          validate_licence: Boolean(formData.validate_licence),
          validate_reglement: Boolean(formData.validate_reglement),
          lien_drive: orNull(formData.lien_drive),
        };

        if (payload.validate_cm && !payload.date_cm) {
          payload.date_cm = todayISO();
        }

        await createDossierInscription(adherentId, payload);
      }

      toast.success("Dossier d'inscription créé avec succès.");

      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur création dossier :", error);

      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Erreur lors de la création du dossier.";

      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DossierDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Créer un dossier d'inscription"
      subtitle="Associez un adhérent à son dossier et ajoutez les premiers documents si besoin."
      width="max-w-5xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <SectionCard
            icon={<FaUserCheck />}
            title="Adhérent"
            subtitle="Le dossier d'inscription est rattaché à un seul adhérent."
          >
            <AdherentPicker
              list={adherents}
              value={formData.inscription_adherent_id}
              disabled={loadingAdherents}
              onChange={(id) =>
                setFormData((prev) => ({
                  ...prev,
                  inscription_adherent_id: id,
                }))
              }
            />
          </SectionCard>

          <SectionCard
            icon={<FaFileAlt />}
            title="Certificat médical"
            subtitle="URL ou chemin du document, date et validation."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <FieldLabel>URL du certificat médical</FieldLabel>
                <input
                  type="text"
                  name="cm_url"
                  value={formData.cm_url}
                  onChange={handleChange}
                  placeholder="https://... ou /uploads/..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Date du certificat</FieldLabel>
                <input
                  type="date"
                  name="date_cm"
                  value={formData.date_cm}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <BigCheck
                name="validate_cm"
                checked={formData.validate_cm}
                onChange={handleChange}
                label="CM validé"
                helper="Coche automatiquement la date du jour si aucune date n'est saisie."
              />
            </div>
          </SectionCard>

          <SectionCard
            icon={<FaFileAlt />}
            title="Licence & assurance"
            subtitle="Formulaires et licence définitive."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel>Formulaire à signer</FieldLabel>
                <input
                  type="text"
                  name="formulaire_licence_a_signer"
                  value={formData.formulaire_licence_a_signer}
                  onChange={handleChange}
                  placeholder="https://... ou /uploads/..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <FieldLabel>Formulaire signé</FieldLabel>
                <input
                  type="text"
                  name="formulaire_licence_signe"
                  value={formData.formulaire_licence_signe}
                  onChange={handleChange}
                  placeholder="https://... ou /uploads/..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="sm:col-span-2">
                <FieldLabel>Licence / Assurance</FieldLabel>
                <input
                  type="text"
                  name="licence_url"
                  value={formData.licence_url}
                  onChange={handleChange}
                  placeholder="https://... ou /uploads/..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <BigCheck
                name="validate_licence"
                checked={formData.validate_licence}
                onChange={handleChange}
                label="Licence validée"
                helper="Validation administrative de la licence."
              />
            </div>
          </SectionCard>

          <SectionCard
            icon={<FaGoogleDrive />}
            title="Drive & règlement"
            subtitle="Lien vers le dossier Drive et validation du règlement."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <FieldLabel>Lien Drive</FieldLabel>
                <input
                  type="text"
                  name="lien_drive"
                  value={formData.lien_drive}
                  onChange={handleChange}
                  placeholder="https://drive.google.com/..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <BigCheck
                name="validate_reglement"
                checked={formData.validate_reglement}
                onChange={handleChange}
                label="Règlement validé"
                helper="Validation du paiement dans le dossier d'inscription."
              />
            </div>
          </SectionCard>

          <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-xs leading-5 text-indigo-800 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
            Tu peux aussi créer un dossier minimal en sélectionnant uniquement un
            adhérent. Les documents pourront être ajoutés ensuite depuis le panneau
            d'édition.
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Création..." : "Créer"}
          </button>
        </div>
      </form>
    </DossierDrawer>
  );
}